// @ts-nocheck
import { memo } from "react";
import { useEffect, useRef, useState, useMemo } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import {
  EVideoInfoParseStatus,
  IVideoClipInfo,
  IVideoFileInfo,
} from "logic/src/objects/video/types";
import styles from "./index.module.scss";
import { getCacheFile } from "logic/src/services/richmodal";
import {
  clipMultipleVideos,
  uploadClippedVideoAndCache,
} from "logic/src/objects/video/utils";

// 组件属性定义
interface VideoClipperProps {
  sessionId: string;
  status: EVideoInfoParseStatus; // 当前数据状态
  videosInfo: IVideoFileInfo[]; // 多视频信息
  clipSegmentObs: IVideoClipInfo[]; // 剪辑片段数组 [[startTime, endTime], ...]
}

const VideoClipper: React.FC<VideoClipperProps> = memo(
  ({ status, videosInfo, clipSegmentObs }) => {
    const [isClipping, setIsClipping] = useState(false); // 剪辑状态
    const [progress, setProgress] = useState(0); // 剪辑进度
    const [clippedVideoBlob, setClippedVideoBlob] = useState<Blob | null>(null); // 剪辑后的视频Blob
    const [clippedVideoUrl, setClippedVideoUrl] = useState<string | null>(null); // 剪辑后的视频URL
    const [isLoading, setIsLoading] = useState(false); // 加载缓存视频状态
    const [loadingError, setLoadingError] = useState<string | null>(null); // 加载错误信息
    const canvasWrapperRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);

    // 判断是否是本地视频链接，使用useMemo缓存结果
    const isLocalVideo = useMemo(() => {
      // 如果没有视频或视频数组为空，返回false
      if (!videosInfo || videosInfo.length === 0 || !videosInfo[0]?.url) {
        return false;
      }

      // 默认使用第一个视频的URL判断
      const url = videosInfo[0].url;

      // 按照可能性和检查速度排序:
      // 1. 以blob:开头的情况最常见且检查最快
      if (url.startsWith("blob:")) {
        return true;
      }

      // 2. 检查data: URL
      if (url.startsWith("data:")) {
        return true;
      }

      // 3. 最后检查包含objectURL的情况
      return url.includes("objectURL");
    }, [videosInfo]);

    // 使用useMemo缓存计算结果，只在视频信息或剪辑片段变化时重新计算
    const cacheKey = useMemo(() => {
      // 检查是否有有效的视频信息
      if (
        !videosInfo ||
        videosInfo.length === 0 ||
        !videosInfo[0]?.fileId ||
        !clipSegmentObs.length
      ) {
        return "";
      }

      // 收集所有视频的fileId
      const fileIds = videosInfo.map(v => v.fileId).filter(Boolean);

      if (fileIds.length === 0) {
        console.warn("没有有效的文件ID，无法生成cacheKey");
        return "";
      }

      // 按照与getCacheKey函数相同的格式生成键 - fileIds用下划线连接
      // 将片段按开始时间排序，并保留视频索引信息
      const sortedSegments = [...clipSegmentObs]
        .sort((a, b) => a.start - b.start)
        .map(item => [item.index - 1, item.start, item.end]);

      console.log("重新计算cacheKey...", fileIds, sortedSegments);

      if (sortedSegments.length === 0) {
        console.warn("没有有效的剪辑片段，无法生成cacheKey");
        return "";
      }

      // 生成缓存键：fileIds用下划线连接 + 排序后的剪辑片段字符串
      // 使用新的格式：包含视频索引、开始时间和结束时间
      return encodeURIComponent(
        fileIds.join("_") + JSON.stringify(sortedSegments),
      );
    }, [videosInfo, clipSegmentObs]);

    // 当状态变为已处理完成时，开始处理视频
    useEffect(() => {
      if (
        status === EVideoInfoParseStatus.end &&
        videosInfo &&
        videosInfo.length > 0 &&
        videosInfo[0]?.url &&
        clipSegmentObs.length > 0
      ) {
        // 判断是否是本地视频链接
        if (isLocalVideo) {
          // 如果是本地视频，进行剪辑
          console.log("检测到本地视频链接，开始剪辑...");
          startClipping();
        } else {
          // startClipping();
          // 如果是在线视频，获取缓存的视频URL
          console.log("检测到在线视频链接，尝试获取缓存...");
          fetchCachedVideo();
        }
      }

      // 如果状态为ERROR，清理可能存在的资源
      if (status === EVideoInfoParseStatus.error) {
        // 重置剪辑状态
        setIsClipping(false);
        setProgress(0);
        setIsLoading(false);
        setLoadingError("服务器异常，请稍后再试...");

        // 如果有URL对象，进行清理
        if (clippedVideoUrl && clippedVideoUrl.startsWith("blob:")) {
          URL.revokeObjectURL(clippedVideoUrl);
          setClippedVideoUrl(null);
        }

        // 清空视频Blob
        setClippedVideoBlob(null);
      }
    }, [status, videosInfo, clipSegmentObs, isLocalVideo]);

    // 获取缓存的视频URL
    const fetchCachedVideo = async () => {
      if (
        !videosInfo ||
        videosInfo.length === 0 ||
        !videosInfo[0]?.fileId ||
        clipSegmentObs.length === 0
      ) {
        console.error("文件ID或剪辑片段不存在");
        return;
      }

      // 验证缓存键是否有效
      if (!cacheKey) {
        console.error("缓存键无效，无法获取缓存视频");
        setLoadingError("无法获取缓存视频：缓存键无效");
        // 缓存键无效时启动剪辑流程
        console.log("缓存键无效，切换到剪辑模式");
        startClipping();
        return;
      }

      setIsLoading(true);
      setLoadingError(null);

      try {
        // 使用缓存的cacheKey
        console.log("尝试获取缓存视频，cacheKey:", cacheKey);

        // 获取缓存的视频URL - 传递数组形式的cacheKey
        const cacheFiles = await getCacheFile([cacheKey]);
        const cachedVideoUrl = cacheFiles?.[0]?.url;

        if (cachedVideoUrl) {
          console.log("成功获取缓存的视频URL:", cachedVideoUrl);
          setClippedVideoUrl(cachedVideoUrl);
          // 创建一个空的blob作为标记，表示已有视频URL
          setClippedVideoBlob(new Blob([], { type: "video/mp4" }));
          setIsLoading(false);
        } else {
          console.warn("未找到缓存的视频，需要重新剪辑");
          setLoadingError(null); // 清除错误，因为将转为剪辑模式
          setIsLoading(false);

          // 未找到缓存视频时自动启动剪辑流程
          console.log("未找到缓存视频，切换到剪辑模式");
          startClipping();
        }
      } catch (error) {
        if (error instanceof Error) {
          console.error("获取缓存视频失败:", error.message);

          // 记录错误，但不显示给用户，因为将切换到剪辑模式
          console.log(`获取缓存失败: ${error.message}，切换到剪辑模式`);
        } else {
          console.error("获取缓存视频失败:", error);
        }

        // 结束加载状态
        setIsLoading(false);
        setLoadingError(null); // 清除错误信息，避免显示错误

        // 缓存获取失败时自动启动剪辑流程
        startClipping();
      }
    };

    // 组件卸载时清理资源
    useEffect(() => {
      return () => {
        // 清理URL对象
        if (clippedVideoUrl && clippedVideoUrl.startsWith("blob:")) {
          URL.revokeObjectURL(clippedVideoUrl);
        }
      };
    }, []);

    // 处理视频导出 - 已被注释掉的UI使用，暂时保留但标记为已忽略
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleExportVideo = () => {
      if (!clippedVideoBlob) return;

      // 根据视频MIME类型选择正确的文件扩展名
      const fileExtension = clippedVideoBlob.type.includes("mp4")
        ? "mp4"
        : "webm";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(clippedVideoBlob);
      a.download = `视频剪辑_${new Date().getTime()}.${fileExtension}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    };

    // 视频剪辑处理函数 - 新的入口点
    const startClipping = async () => {
      // 检查视频数组
      if (!videosInfo || videosInfo.length === 0) {
        console.error("没有可用的视频");
        return;
      }

      // 检查剪辑片段
      // 将剪辑片段转换为包含视频索引的格式 [索引, 开始时间, 结束时间]
      const clipSegments = clipSegmentObs.map(item => [
        item.index - 1, // 索引从1开始，转为从0开始
        item.start,
        item.end,
      ]);

      if (clipSegments.length === 0) {
        console.error("剪辑片段不存在");
        return;
      }

      setIsClipping(true);
      setProgress(0);

      try {
        // 使用utils中的多视频剪辑方法
        console.log("开始多视频剪辑处理", clipSegments);
        const finalBlob = await clipMultipleVideos(
          videosInfo,
          clipSegments,
          setProgress,
        );

        // 剪辑完成，立即设置结果并展示视频
        setClippedVideoBlob(finalBlob);
        const url = URL.createObjectURL(finalBlob);
        setClippedVideoUrl(url);

        // 立即结束剪辑加载状态，让用户可以看到视频
        setIsClipping(false);

        // 在后台异步上传视频，不阻塞用户界面
        console.log("剪辑完成，视频已显示，正在后台上传...");
        uploadClippedVideoAndCacheVideo(finalBlob).catch(error => {
          console.error("视频上传过程中出错:", error);
          // 上传失败不影响用户观看视频，只在控制台显示错误
        });
      } catch (error) {
        console.error("视频剪辑过程中出错:", error);
        setLoadingError("视频剪辑失败，请稍后再试");
        setIsClipping(false);
      }
    };

    /**
     * 上传剪辑后的视频到OSS并缓存URL
     * @param videoBlob 剪辑后的视频Blob
     */
    const uploadClippedVideoAndCacheVideo = async (videoBlob: Blob) => {
      try {
        if (
          !videosInfo ||
          videosInfo.length === 0 ||
          !videosInfo[0]?.fileId ||
          !clipSegmentObs.length
        ) {
          console.error("缺少必要的视频信息或剪辑片段");
          setLoadingError("无法缓存视频：缺少必要信息");
          return;
        }

        // 使用组件上方已计算好的cacheKey，而不是重新生成
        if (!cacheKey) {
          console.error("缓存键无效");
          setLoadingError("无法缓存视频：缓存键无效");
          return;
        }

        console.log("开始上传视频并缓存，使用组件级缓存键:", cacheKey);

        // 调用工具函数上传并缓存，使用一致的cacheKey
        const result = await uploadClippedVideoAndCache(videoBlob, cacheKey);

        if (result) {
          console.log("视频上传并缓存成功");
        } else {
          console.warn("视频上传或缓存部分失败");
          setLoadingError("视频缓存部分失败，但您仍可继续使用当前剪辑的视频");
        }
      } catch (error) {
        if (error instanceof Error) {
          console.error("上传视频并缓存URL失败:", error.message);

          // 根据错误类型提供不同的错误提示
          if (error.message.includes("无商量权限")) {
            setLoadingError("您没有权限缓存视频，请联系管理员");
          } else if (error.message.includes("参数错误")) {
            setLoadingError("视频缓存失败：参数错误，请重试");
          } else if (error.message.includes("内部错误")) {
            setLoadingError("服务器内部错误，请稍后再试");
          } else {
            setLoadingError(`视频缓存失败: ${error.message}`);
          }
        } else {
          console.error("上传视频并缓存URL失败:", error);
          setLoadingError("视频缓存失败，请稍后再试");
        }
      }
    };

    /**
     * 生成缓存键
     * 根据文件ID和剪辑片段信息生成唯一的缓存键
     * @param fileIds 文件ID数组
     * @param clipSegments 剪辑片段数组
     * @returns 格式化的缓存键字符串
     */

    // 如果状态为处理中，不进行剪辑
    if (status === EVideoInfoParseStatus.parsing) {
      return <p className={styles.loadingText}>正在努力处理中...</p>;
    }

    if (status === EVideoInfoParseStatus.error) {
      return null;
    }

    return (
      <div className={styles.videoClipperContainer}>
        {/* 隐藏的canvas容器，用于视频处理 */}
        <div ref={canvasWrapperRef} style={{ display: "none" }}></div>

        {isClipping ? (
          <div className={styles.loadingContainer}>
            <LoadingOutlined className={styles.loadingIcon} />
            <p className={styles.loadingText}>正在努力剪辑中...{progress}%</p>
          </div>
        ) : isLoading ? (
          <div className={styles.loadingContainer}>
            <LoadingOutlined className={styles.loadingIcon} />
            <p className={styles.loadingText}>正在获取视频...</p>
          </div>
        ) : status === EVideoInfoParseStatus.error || loadingError ? (
          <div className={styles.errorContainer}>
            <p className={styles.errorText}>
              {loadingError || "服务器异常，剪辑失败，请稍后再试..."}
            </p>
          </div>
        ) : clippedVideoBlob ? (
          <div className={styles.clippedVideoContainer}>
            {/* 尝试播放视频 */}
            <div className={styles.videoPreview}>
              <video
                preload="auto"
                ref={videoRef}
                src={clippedVideoUrl || undefined}
                controls
                className={styles.clippedVideo}
                onError={e => {
                  console.error("视频播放错误:", e);
                  setLoadingError("视频播放失败，请尝试重新剪辑");
                }}
              />
            </div>
          </div>
        ) : null}
      </div>
    );
  },
);

// 添加displayName
VideoClipper.displayName = "VideoClipper";

export default VideoClipper;
